import { FC, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Center,
  Flex,
  HStack,
  useRadioGroup,
  VStack,
  Text,
  SimpleGrid,
  Spinner,
  Image,
  useBreakpointValue,
} from '@chakra-ui/react'
import { RadioButtonProps } from './interfaces'
import { SystemProps } from '@chakra-ui/system'
import { DefaultCheckbox } from '../checkboxes/default-checkbox'
import { useTranslation } from 'react-i18next'
import { addAnswer, selectAnswers, selectCurrentStep } from '../../state'
import { cardImageMap, Steps } from '../../state/interfaces'
import { useDispatch, useSelector } from 'react-redux'
import { selectScenarioIsWaitingForAnswer } from '../../state'
import { LoadingNewAnswer } from '../spinners/loading-new-answer'

interface Choice {
  value: string
  label: string
  image?: string
  imageSelected?: string
  isChecked?: boolean
}

export interface RadioButtonGroupProps {
  name: string
  choices: Choice[]
  value?: string
  onChange?: (values: string[]) => void
  component: FC<RadioButtonProps>
  justify?: SystemProps['justifyContent']
  direction?: 'horizontal' | 'vertical'
  options?: any
  grandImage?: string
}

export const RadioButtonGroup: FC<RadioButtonGroupProps> = ({
  choices,
  value,
  onChange,
  name,
  component: Component,
  direction = 'horizontal',
  options,
  grandImage,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const currentStep = useSelector(selectCurrentStep)
  const image = useMemo(() => (grandImage ? cardImageMap[grandImage] : ''), [grandImage])
  const { getRadioProps, getRootProps } = useRadioGroup({
    onChange: (value) => {
      const timeOut = currentStep && currentStep.id === Steps.DECISION_MOBILE_INTERNET ? 350 : 0
      if (currentStep && currentStep.id === Steps.HOW_MANY_CALLING_MINUTES) {
        if (options) {
          dispatch(addAnswer({ stepId: Steps.CALLING_OPTIONS, answerIds: selectedChoices, hidden: true }))
        }
      }
      setTimeout(() => {
        return onChange && onChange([value])
      }, timeOut)
    },
    value,
    name,
  })
  const Stack = useMemo<typeof HStack | typeof VStack>(
    () => (direction === 'horizontal' ? HStack : VStack),
    [direction],
  )
  const isDesktop = useBreakpointValue({ base: false, lg: true })
  const [selectedImage, setSelectedImage] = useState('')
  const handleImageClick = (image: string) => {
    setSelectedImage(image)
  }
  const [selectedChoices, setSelectedChoices] = useState<string[]>([])
  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (checked) {
      setSelectedChoices((prevSelected) => [...prevSelected, value])
    } else {
      setSelectedChoices((prevSelected) => prevSelected.filter((val) => val !== value))
    }
  }
  const stepsWithFalseButton = [Steps.START_RATING, Steps.THANKS]
  const isStepwithFalseButton = currentStep ? stepsWithFalseButton.includes(currentStep.id as Steps) : false

  useEffect(() => {
    if (options) {
      options.forEach((option: any) => {
        if (option.isChecked) {
          setSelectedChoices((prevSelected) => [...prevSelected, option.value])
        }
      })
    }
  }, [])

  const scenarioIsWaitingForAnswer = useSelector(selectScenarioIsWaitingForAnswer)
  const answers = useSelector(selectAnswers)

  return (
    <VStack>
      {answers &&
      answers['buy-or-bring-phone-quick-search'] &&
      answers['buy-or-bring-phone-quick-search'].includes('finishScenario') ? (
        <Spinner />
      ) : (
        <>
          <Center>
            {options && (
              <VStack>
                <Flex direction="column" marginTop={5}>
                  <Text my={4}>{t('common_options')}:</Text>
                  <Flex justifyContent="center" mb={5}>
                    <SimpleGrid columns={3} spacingX={6} spacingY={4}>
                      {options
                        .filter((option: any) => option.group === 'options')
                        .map((option: any) => (
                          <Box key={option.value}>
                            <DefaultCheckbox
                              key={option.value}
                              choice={option}
                              isChecked={option.isChecked}
                              onChange={handleCheckboxChange}
                            />
                          </Box>
                        ))}
                    </SimpleGrid>
                  </Flex>
                </Flex>
              </VStack>
            )}
          </Center>
          <Center>
            <Stack>
              <Flex marginTop={5} alignItems="center">
                {isDesktop && image && !isStepwithFalseButton && (
                  <Box marginRight={1}>
                    <div style={{ flex: '60%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Image src={image} alt="image" width="80%" />
                    </div>
                  </Box>
                )}
                <VStack>
                  <Stack
                    {...getRootProps()}
                    width={isDesktop && image ? '50%' : '100%'}
                    justifyContent="center"
                    alignItems="center"
                    spacing={[0, 2]}
                    flexWrap="wrap"
                    margin="2vh"
                  >
                    {choices.map((props) =>
                      props.image ? (
                        <div key={`choice-${props.value}`} onClick={() => handleImageClick(props.imageSelected || '')}>
                          <Component
                            {...getRadioProps({ value: props.value })}
                            {...props}
                            image={selectedImage === props.imageSelected ? props.imageSelected : props.image}
                          />
                        </div>
                      ) : scenarioIsWaitingForAnswer ? (
                        <LoadingNewAnswer key={`choice-${props.value}`} />
                      ) : (
                        <Component
                          key={`choice-${props.value}`}
                          {...getRadioProps({ value: props.value })}
                          {...props}
                        />
                      ),
                    )}
                  </Stack>
                  {image && isStepwithFalseButton && (
                    <Box marginRight={1}>
                      <div style={{ flex: '60%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Image src={image} alt="image" width={'60%'} height={'60%'} />
                      </div>
                    </Box>
                  )}
                </VStack>
              </Flex>
              <Flex direction="column" alignItems="center" justifyContent="center">
                {/* Other components */}
                {!isDesktop && image && !isStepwithFalseButton && (
                  <Center>
                    <Image src={image} alt="image" width="80%" />
                  </Center>
                )}
              </Flex>
            </Stack>
          </Center>
        </>
      )}
    </VStack>
  )
}
